/**
 * Serviço de Web Push do app de talentos.
 *
 * Encapsula toda a mecânica de Push API/Service Worker e fala com a API
 * (rotas /api/v2/talents/push/*).
 */
import api from './api'

const SW_URL = '/service-worker.js'
const VAPID_URL = '/api/v2/talents/push/vapid-public-key'
const SUBSCRIPTIONS_URL = '/api/v2/talents/push/subscriptions'

// O endpoint é o segredo que permite enviar push para este aparelho, então ele
// só trafega no corpo da requisição — nunca em query string, que vai parar em
// log de acesso. O uuid é identificador de linha e pode circular, por isso é
// ele que guardamos para reconhecer esta inscrição no servidor.
const DEVICE_UUID_KEY = 'push.deviceUuid.v1'

// Baixas que não chegaram à API (rede caiu no meio do "desligar"). Sem isto, um
// opt-out deliberado só apareceria no servidor mais tarde, como falha de envio,
// e seria contado como aparelho morto em vez de escolha da pessoa.
const PENDING_REMOVALS_KEY = 'push.pendingRemovals.v1'

function readStorage(key) {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // localStorage indisponível (aba anônima, storage cheio): seguimos sem ele.
  }
}

function clearStorage(key) {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    // idem
  }
}

/** O uuid da inscrição deste navegador, se já o conhecemos. */
export function currentDeviceUuid() {
  return readStorage(DEVICE_UUID_KEY)
}

function readPendingRemovals() {
  const raw = readStorage(PENDING_REMOVALS_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function enqueuePendingRemoval(payload) {
  const pending = readPendingRemovals()
  pending.push(payload)
  writeStorage(PENDING_REMOVALS_KEY, JSON.stringify(pending))
}

/** O navegador suporta Web Push? */
export function isSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function isIOS() {
  const ua = navigator.userAgent
  // O Safari do iPadOS 13+ se apresenta como "Macintosh", e sem este desempate o
  // iPad nao cai em ramo nenhum: nao tem PushManager na aba, entao `isSupported`
  // e falso, e `iosNeedsInstall` tambem -- o convite some e Configuracoes manda
  // "abra no Chrome", conselho que nao funciona em navegador nenhum do iPadOS.
  // Mac de verdade nao reporta mais de um ponto de toque.
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  return /iphone|ipad|ipod/i.test(ua) || isIPadOS
}

/** O app está rodando como PWA instalado (tela inicial), não numa aba? */
export function isStandalone() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

/**
 * No iOS, Web Push só funciona com o app adicionado à Tela de Início.
 * Detecta o caso "iPhone numa aba do Safari" para mostrarmos instruções.
 */
export function iosNeedsInstall() {
  return isIOS() && !isStandalone()
}

export function permission() {
  return 'Notification' in window ? Notification.permission : 'denied'
}

/** Já existe uma subscription ativa neste navegador? */
export async function isSubscribed() {
  if (!isSupported()) return false
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return false
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i)
  return output
}

function describeDevice() {
  const ua = navigator.userAgent
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /CriOS/.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Navegador'
  const os = /iPhone|iPad|iPod/.test(ua) ? 'iPhone/iPad'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'Mac'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : 'dispositivo'
  return `${browser} · ${os}`
}

async function registerServiceWorker() {
  const reg = await navigator.serviceWorker.register(SW_URL)
  await navigator.serviceWorker.ready
  return reg
}

/**
 * Pede permissão, registra o SW, inscreve no push e envia a subscription à API.
 * Lança erro com mensagem amigável nos casos de falha (permissão negada, etc.).
 */
export async function subscribe() {
  if (!isSupported()) {
    throw new Error('UNSUPPORTED')
  }

  const result = await Notification.requestPermission()
  if (result !== 'granted') {
    throw new Error(result === 'denied' ? 'DENIED' : 'DISMISSED')
  }

  const reg = await registerServiceWorker()

  const { data } = await api.get(VAPID_URL)
  const publicKey = data && data.public_key
  if (!publicKey) throw new Error('NO_VAPID_KEY')

  let sub = await reg.pushManager.getSubscription()
  const createdNow = !sub
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    } catch (error) {
      const pushUnavailable = (
        error &&
        error.name === 'AbortError' &&
        /push service not available/i.test(error.message || '')
      )

      if (pushUnavailable) {
        throw new Error('PUSH_SERVICE_UNAVAILABLE')
      }

      throw error
    }
  }

  const json = sub.toJSON()

  try {
    const response = await api.post(SUBSCRIPTIONS_URL, {
      endpoint: json.endpoint,
      keys: json.keys,
      device_label: describeDevice(),
    })

    const uuid = response && response.data && response.data.uuid
    if (uuid) writeStorage(DEVICE_UUID_KEY, uuid)
  } catch (e) {
    // Sem desfazer, o navegador fica inscrito e o servidor sem registro: o
    // convite some da tela, a permissao ja concedida impede o prompt de voltar,
    // e a pessoa acha que ativou e nunca recebe nada.
    //
    // Desfaz SO o que acabamos de criar. Uma inscricao que ja existia pode estar
    // registrada no servidor sob um uuid que perdemos, e apaga-la aqui deixaria
    // la um endpoint morto -- o oposto do que se quer.
    if (createdNow) await sub.unsubscribe()
    throw e
  }

  return true
}

/**
 * Pede à API para apagar uma inscrição.
 *
 * Manda uuid OU endpoint, nunca os dois: hoje o `destroy()` dá precedência ao
 * endpoint, então enviar os dois apagaria o aparelho onde a tela está aberta em
 * vez do que a pessoa escolheu na lista.
 */
function requestRemoval({ uuid, endpoint, reason }) {
  const params = new URLSearchParams()
  if (reason) params.set('reason', reason)

  if (uuid) {
    params.set('uuid', uuid)
    const query = params.toString()
    return api.delete(`${SUBSCRIPTIONS_URL}?${query}`)
  }

  const data = { endpoint }
  if (reason) data.reason = reason
  return api.delete(SUBSCRIPTIONS_URL, { data })
}

/**
 * Desliga os avisos NESTE aparelho.
 *
 * Além de apagar a linha no servidor, destrói a subscription do navegador — é
 * o que faz o opt-out grudar, porque sem endpoint não há como reinscrever sem
 * um novo clique. Se a API não responder, a pessoa para de receber na mesma
 * hora e a baixa fica na fila para ser reenviada na próxima abertura do app.
 */
export async function unsubscribeCurrentDevice({ reason } = {}) {
  if (!isSupported()) return
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return

  const uuid = currentDeviceUuid()
  const endpoint = sub.endpoint

  try {
    await requestRemoval({ uuid, endpoint, reason })
  } catch (e) {
    enqueuePendingRemoval(uuid ? { uuid, reason } : { endpoint, reason })
  }

  clearStorage(DEVICE_UUID_KEY)
  await sub.unsubscribe()
}

/**
 * Remove OUTRO aparelho da lista, pelo uuid.
 *
 * Nunca toca na subscription do navegador: quem está com a tela aberta é o
 * aparelho atual, e desinscrevê-lo aqui desligaria justamente quem não foi
 * escolhido.
 */
export async function removeDevice(uuid) {
  if (!uuid) throw new Error('MISSING_UUID')
  if (uuid === currentDeviceUuid()) {
    return unsubscribeCurrentDevice()
  }
  await requestRemoval({ uuid })
}

/** Os aparelhos com avisos ligados nesta conta. */
export async function listDevices() {
  const { data } = await api.get(SUBSCRIPTIONS_URL)
  return (data && data.data) || []
}

/**
 * Vale insistir nesta baixa na próxima abertura do app?
 *
 * Sem rede a API nem respondeu, então a tentativa continua de pé. Já uma
 * resposta 4xx é definitiva — a inscrição não existe mais, ou não é desta
 * conta — e reenfileirar faria a fila crescer para sempre sem nunca drenar.
 */
function isWorthRetrying(error) {
  const status = error && error.response && error.response.status
  if (!status) return true
  if (status === 408 || status === 429) return true
  return status >= 500
}

/**
 * Reenvia baixas que não chegaram à API.
 *
 * Uma baixa perdida não some: ela reaparece depois como falha de envio e é
 * contada como aparelho morto, escondendo que a pessoa desligou de propósito.
 * Quem nunca mais abrir o app continua invisível — isso não tem conserto pelo
 * lado do cliente.
 */
export async function flushPendingRemovals() {
  const pending = readPendingRemovals()
  if (!pending.length) return

  const stillPending = []
  for (const item of pending) {
    try {
      await requestRemoval(item)
    } catch (e) {
      if (isWorthRetrying(e)) stillPending.push(item)
    }
  }

  if (stillPending.length) {
    writeStorage(PENDING_REMOVALS_KEY, JSON.stringify(stillPending))
  } else {
    clearStorage(PENDING_REMOVALS_KEY)
  }
}

/**
 * Confere o estado local contra o servidor e devolve se este aparelho está
 * mesmo ligado.
 *
 * Sem isto o app mente: quem foi removido a partir de outro aparelho continua
 * com a subscription viva no navegador, então o estado local diria "ligado"
 * enquanto nenhuma notificação chega e não há nada para clicar.
 */
export async function syncSubscriptionState() {
  if (!isSupported()) return false

  const reg = await navigator.serviceWorker.getRegistration()
  const sub = reg ? await reg.pushManager.getSubscription() : null
  if (!sub) {
    clearStorage(DEVICE_UUID_KEY)
    return false
  }

  const uuid = currentDeviceUuid()
  // Sem uuid guardado não há como cruzar: a lista do servidor não expõe
  // endpoint, e `device_label` não desempata dois Chrome no mesmo Android.
  if (!uuid) return true

  let devices
  try {
    devices = await listDevices()
  } catch (e) {
    // Offline ou API fora: o estado local é a melhor informação disponível.
    return true
  }

  const stillRegistered = devices.some((device) => device.uuid === uuid)
  if (stillRegistered) return true

  clearStorage(DEVICE_UUID_KEY)
  await sub.unsubscribe()
  return false
}

export default {
  isSupported,
  isIOS,
  isStandalone,
  iosNeedsInstall,
  permission,
  isSubscribed,
  currentDeviceUuid,
  subscribe,
  unsubscribeCurrentDevice,
  removeDevice,
  listDevices,
  flushPendingRemovals,
  syncSubscriptionState,
}
